import { useEffect, useState } from "react";
import { weaponLabel } from "./weapons.ts";

/**
 * CS2's own weapon, equipment and kill-feed icons (assets/cs2, from the game files):
 * white shapes on a transparent ground, about 32 units high, as wide as the weapon.
 */
const URLS = import.meta.glob("../assets/cs2/*.svg", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;
const BY_NAME = new Map(
  Object.entries(URLS).map(([path, url]) => [
    path
      .split("/")
      .pop()!
      .replace(/\.svg$/, ""),
    url,
  ]),
);

/** Other names the game and demos use for the same thing. */
const ALIAS: Record<string, string> = {
  m4a4: "m4a1",
  m4a1_silencer_off: "m4a1_silencer",
  usp_silencer_off: "usp_silencer",
  p2000: "hkp2000",
  molotovgrenade: "molotov",
  incendiarygrenade: "incgrenade",
  decoygrenade: "decoy",
  smoke: "smokegrenade",
  he: "hegrenade",
  flash: "flashbang",
  vest: "kevlar",
  vesthelm: "assaultsuit",
  item_kevlar: "kevlar",
  item_assaultsuit: "assaultsuit",
  item_defuser: "defuser",
  kit: "defuser",
  knifegg: "knife",
  // Kill feed marks.
  headshot: "icon_headshot",
  wallbang: "penetrate",
  through_smoke: "smoke_kill",
  blind: "blind_kill",
  air: "inairkill",
  suicide: "icon_suicide",
};

/** The icon for a weapon or mark as demos name it ("weapon_ak47", "knife_karambit", "headshot"), if there is one. */
export function cs2IconName(name: string): string | null {
  const key = name.toLowerCase().replace(/^weapon_/, "");
  const n = ALIAS[key] ?? key;
  if (BY_NAME.has(n)) return n;
  if (n.includes("knife") || n === "bayonet") return "knife";
  return null;
}

export const cs2IconUrl = (name: string): string | undefined => {
  const n = cs2IconName(name);
  return n ? BY_NAME.get(n) : undefined;
};

/** An icon at the text's height; with a label it is read out, without one it is decoration. */
export function Cs2Icon({ name, label, className = "" }: { name: string; label?: string; className?: string }) {
  const url = cs2IconUrl(name);
  if (!url) return label ? <span className={`cs2-icon-text ${className}`}>{label}</span> : null;
  return <img src={url} alt={label ?? ""} aria-hidden={label ? undefined : true} title={label} className={`cs2-icon ${className}`} draggable={false} />;
}

/** A weapon's icon, named for screen readers ("AK-47"). */
export const WeaponIcon = ({ weapon, className }: { weapon: string; className?: string }) => <Cs2Icon name={weapon} label={weaponLabel(weapon)} className={className} />;

// ---- for canvases: every icon loaded once per visit

const images = new Map<string, HTMLImageElement>();
let loading: Promise<void> | null = null;

function loadAll(): Promise<void> {
  loading ??= Promise.all(
    [...BY_NAME].map(
      ([name, url]) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => {
            images.set(name, img);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = url;
        }),
    ),
  ).then(() => undefined);
  return loading;
}

/** The loaded image of an icon, for drawing; null until `useCs2Icons` has loaded them. */
export function cs2Image(name: string): HTMLImageElement | null {
  const n = cs2IconName(name);
  return n ? (images.get(n) ?? null) : null;
}

/** Loads every icon (about 60 small files) for canvases; true once they are there. */
export function useCs2Icons(): boolean {
  const [ready, setReady] = useState(() => images.size === BY_NAME.size);
  useEffect(() => {
    if (ready) return;
    let live = true;
    void loadAll().then(() => live && setReady(true));
    return () => {
      live = false;
    };
  }, [ready]);
  return ready;
}
